import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, AlertTriangle, Calendar, CheckSquare, ArrowRight } from "lucide-react";

const SITUACAO_COLORS = {
  "Novo": "bg-yellow-100 text-yellow-800",
  "Em curso": "bg-blue-100 text-blue-800",
  "Validar": "bg-purple-100 text-purple-800",
  "Findo": "bg-green-100 text-green-800",
  "Aguarda Documentos/Informações": "bg-orange-100 text-orange-800",
  "Aguarda Pagamento": "bg-indigo-100 text-indigo-800",
};

function KpiCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function getGreeting(hour) {
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Carga única de stats (API/axios/getAuthHeader são referências de módulo estáveis)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() })
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"></div>
    </div>
  );

  const now = new Date();
  const greeting = getGreeting(now.getHours());

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{greeting}, {user?.nome}!</h1>
        <p className="text-slate-500 text-sm mt-1">Aqui está o resumo do portal de hoje.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Briefcase} label="Total Processos" value={stats?.total_processos ?? 0} color="bg-[#0b679e]" testId="kpi-processos" />
        <KpiCard icon={AlertTriangle} label="Urgentes" value={stats?.urgentes ?? 0} color="bg-red-500" testId="kpi-urgentes" />
        <KpiCard icon={Calendar} label="Reuniões Hoje" value={stats?.reunioes_hoje ?? 0} color="bg-[#F5A623]" testId="kpi-reunioes" />
        <KpiCard icon={CheckSquare} label="Tarefas Pendentes" value={stats?.tarefas_pendentes ?? 0} color="bg-emerald-500" testId="kpi-tarefas" />
      </div>

      {/* Recent + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Processos */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Processos Recentes</h2>
            <button onClick={() => navigate("/processos")} data-testid="see-all-processos"
              className="text-xs text-[#0b679e] hover:text-[#F5A623] font-medium flex items-center gap-1 transition-colors">
              Ver todos <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {stats?.recent_processos?.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-6">Sem processos ainda</p>
            )}
            {stats?.recent_processos?.map(p => (
              <div key={p.id} onClick={() => navigate(`/processos/${p.id}`)}
                data-testid={`recent-processo-${p.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-200">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{p.nome_cliente || "—"}</p>
                  <p className="text-xs text-slate-500">Nº {p.numero_processo}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {p.prioridade === "Urgente" && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Urgente</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SITUACAO_COLORS[p.situacao] || "bg-slate-100 text-slate-700"}`}>
                    {p.situacao}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Tasks */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Tarefas Pendentes</h2>
            <button onClick={() => navigate("/tarefas")} data-testid="see-all-tarefas"
              className="text-xs text-[#0b679e] hover:text-[#F5A623] font-medium flex items-center gap-1 transition-colors">
              Ver todas <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {stats?.pending_tasks?.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-6">Sem tarefas pendentes</p>
            )}
            {stats?.pending_tasks?.map(t => (
              <div key={t.id} onClick={() => navigate("/tarefas")}
                data-testid={`pending-task-${t.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-200">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-900 line-clamp-1">{t.descricao || "—"}</p>
                  {t.data && <p className="text-xs text-slate-500 mt-0.5">{t.data}</p>}
                </div>
                {t.prioridade === "Urgente" && (
                  <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Urgente</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
